import React, { useEffect, useState } from 'react';

import '../Config.scss';
import { Button, ModalHeader, ModalBody, ModalFooter, TextField, Row, MessageBlock, MessageBlockContent, Col, Dropdown } from 'cfa-react-components';
import { DefaultReplayConfig, IReplayConfig } from 'interfaces';
import { ReplayConfigService, parseAxiosError } from 'services';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IReplayConfigModalProps {
    config: IReplayConfig,
    editing: boolean,
    onHide: () => void,
}

export function ReplayConfigModal({ config, editing, onHide }: IReplayConfigModalProps) {
    
    const { control, formState: {isDirty, isValid}, getValues, reset } = useForm<IReplayConfig>({
        mode: 'onTouched',
        defaultValues: config,
    });
    const { fields: pathFields, append: pathAppend, remove: pathRemove } = useFieldArray({
        control, 
        name: "sinkParams.paths",
    });
    const { fields: mappingFields, append: mappingAppend, remove: mappingRemove } = useFieldArray({
        control, 
        name: "sinkParams.mappings",
    });

    const sinkType = useWatch({control, name: "sinkType"});
    const [copyConfig, setCopyConfig] = useState<IReplayConfig | null>(null);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const copyExistingConfig = (c: IReplayConfig) => {
        setCopyConfig(c);
        reset(c);
    }
    
    const submitReplayConfig = () => {
        
        setLoading(true);
        setErrorMessage(null);

        let payload: IReplayConfig = getValues();

        // put config if editing
        editing && ReplayConfigService.putReplayConfig(config.replayConfigUUID, payload)
            .then(() => {
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })

        // post config if creating new
        !editing && ReplayConfigService.postReplayConfig(payload)
            .then(() => {
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })
    }

    return (<>

        <ModalHeader>
            {editing ? 'Edit Replay Config' : 'New Replay Config'}
        </ModalHeader>

        <ModalBody>
            {errorMessage && (<Row style={{justifyContent: 'center', }}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <div style={{textAlign: "left"}}>
                {!editing && <Row>
                    <Col>
                        <Dropdown
                            label='Copy from existing'
                            fullWidth
                            placeholder='Select (Optional)'
                            value={copyConfig}
                            onChange={(newVal) => copyExistingConfig(newVal)}
                            options={[DefaultReplayConfig, ...ReplayConfigService.allReplayConfigs]}
                            renderOption={(c) => <>{c.name != "" ? c.name : "Default"}</>}
                            // must have these options, since we're passing in an array of objects
                            getOptionText={(c) => c.name}
                            getOptionId={(c) => c.replayConfigUUID}
                        />
                    </Col>
                </Row>}
                <Row>
                    <Col>
                        <Controller name="name" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Name"
                                spellCheck={false}
                                placeholder="Enter name"
                                required
                                {...field}
                            /> 
                        }/>
                    </Col>
                    <Col>
                        <Controller name="sinkType" control={control} rules={{required: true}} render={(field) => 
                            <Dropdown
                                fullWidth
                                label="Sink type"
                                options={["mqtt", "csv"]}
                                {...field}
                            />
                        }/>
                    </Col>
                </Row>
                <Col>
                    {sinkType == "mqtt" && <Row>
                        <Controller name="sinkParams.secretPath" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Secret Name"
                                spellCheck={false}
                                placeholder="Enter broker credentials secret name"
                                required
                                {...field}
                            /> 
                        }/>
                    </Row>}
                    {sinkType != "csv" && <Row>
                        <Col style={{paddingLeft: 0, paddingRight: 0}}>
                            <Controller name="sinkParams.location" control={control} rules={{required: true}} render={(field) => 
                                <TextField
                                    label={sinkType == "mqtt" ? "Broker Url" : "S3 Bucket Url"}
                                    spellCheck={false}
                                    placeholder={sinkType == "mqtt" ? "Enter broker url" : "Enter S3 bucket url"}
                                    required
                                    {...field}
                                />
                            }/>
                        </Col>
                        {sinkType == "csv" && <Col lg={4} style={{paddingTop: 24, paddingLeft: 15}}>
                            <Button onClick={() => pathAppend({url: ""})}>Add Key</Button>
                        </Col>}
                    </Row>}
                    {pathFields.map((item, index) =>
                        <Row key={item.id}>
                            <Col style={{paddingLeft: 0}}>
                                {/* make sure to set defaultValue to make controlled input */}
                                <Controller name={`sinkParams.paths.${index}.url`} control={control} defaultValue={item.url} rules={{required: sinkType=="csv"}} render={(field) => 
                                    <TextField
                                        label={sinkType == "mqtt" ? "Topic" : "S3 Key"}
                                        spellCheck={false}
                                        placeholder={sinkType == "mqtt" ? "Enter mqtt topic" : "Enter S3 key"}
                                        required
                                        {...field}
                                    />
                                }/>
                            </Col>
                            <Col lg={4} style={{paddingTop: 24}}>
                                <Button onClick={() => pathRemove(index)}><FontAwesomeIcon icon="trash" /></Button>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col>
                    {mappingFields.map((item, index) => 
                        <Row key={item.id}>
                            <Col style={{paddingLeft: 0}}>
                                <Controller name={`sinkParams.mappings.${index}.from`} control={control} defaultValue={item.from} rules={{required: true}} render={(field) => 
                                    <TextField
                                        label={"Map From Topic"}
                                        spellCheck={false}
                                        placeholder={"Enter topic to map from"}
                                        required
                                        {...field}
                                    />
                                }/>
                            </Col>
                            <Col>
                                <Controller name={`sinkParams.mappings.${index}.to`} control={control} defaultValue={item.to} rules={{required: true}} render={(field) => 
                                    <TextField
                                        label={"Map To Topic"}
                                        spellCheck={false}
                                        placeholder={"Enter topic to map to"}
                                        required
                                        {...field}
                                    />
                                }/>
                            </Col>
                            <Col lg={4} style={{paddingTop: 24}}>
                                {index == 0 && <Button onClick={() => mappingAppend({from: "", to: ""})}><FontAwesomeIcon icon="plus"/></Button>}
                                {index != 0 && <Button onClick={() => mappingRemove(index)}><FontAwesomeIcon icon="trash" /></Button>}
                            </Col>
                        </Row>
                    )}
                </Col>
                {sinkType == "mqtt" && <Row>
                    <Col>
                        <Controller name="timingOption" control={control} render={(field) => 
                            <Dropdown
                                fullWidth
                                label="Timing Option"
                                options={["actual", "flood", "stated"]}
                                {...field}
                            /> 
                        }/>
                    </Col>
                </Row>}
            </div>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            <Button variant="filled" type="submit" loading={loading} onClick={submitReplayConfig} disabled={!isDirty || !isValid}>
                {!editing && (loading ? 'Submitting...' : 'Submit')}
                {editing && (loading ? 'Saving...' : 'Save')}
            </Button>
        </ModalFooter>

    </>);
}
